/**
 * Integrytas Group - Base App (MOBILE)
 * Page: CSM.USER.PROVISION — mobile-native user-provisioning inbox.
 *
 * Touch-first counterpart of src/pages/users/UserProvisionPage.tsx. A DISTINCT
 * presentation (dense card list + scope chips + status pills + bottom-sheet
 * detail with per-role approve/reject toggles) over the SAME data/logic layer as
 * the desktop page — never the desktop grid re-hosted (§16):
 *   - reads via apiDb: apr_request (+ apr_step for the waiting approver
 *     status and job labels, plus csm_user_master joins to resolve requester /
 *     current-approver ids to names — §17.B4, never a raw id/code at render), and
 *     apr_request_line / apr_step_line for the detail sheet, all resolved
 *     against the ACTIVE locale (§8).
 *   - approve/reject decisions route through apr_decide_lines, the
 *     module's sanctioned RPC (mirrors the desktop RequestDetailModal). That RPC IS
 *     the approval write path — it advances/finalizes the workflow and commits
 *     through the Common Routine (§2). It is an RPC, not a direct apiDb
 *     insert/update/delete, so routed_writes is intentionally not used here (same
 *     precedent as MobileMyApprovalsPage's apr_decide).
 *
 * Three segmented tabs (mock C08): "Submit new user" (a mobile-native form —
 * MobileSubmitNewUserForm — over the same csm_provision_submit RPC as the
 * desktop), "Pending my approval", and "My submissions". The two inbox tabs share
 * the list/detail presentation below; the submit tab renders the form.
 */

/* rbac-affordance-ok: as the desktop page — signoff is refused unless the
   caller is the request's current_approver_id (CSM.PROVISION.NOT_YOUR_TURN),
   which is per-request state, not a page permission. */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, UserPlus, Check, Ban, X } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useFormatDateTime } from '../../lib/format_date';
import { getForensicActorId } from '../../types/auth';
import type { AuthSession } from '../../types/auth';
import { MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileStatusPill } from '../components';
import { MobileRelatedChildren } from '../components/MobileRelatedChildren';
import type { MobileLeadTone, MobilePillTone } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { MobileSubmitNewUserForm } from './MobileSubmitNewUserForm';

interface TlRow { locale_code: string; display_text: string }

/** Per-role decision status on csm_user_provision_request_role. */
type RoleStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
type Decision = 'APPROVE' | 'REJECT';
/** Top-level tab: submit a new request, approve pending ones, or view my own. */
type Tab = 'submit' | 'pending' | 'mine';

interface RequestRow {
  /** apr_request.approval_request_id — provisioning runs on APR now. */
  approval_request_id: number;
  request_internal_code: string;
  target_email: string;
  target_display_name: string;
  target_job_label: string;
  requested_by: number;
  requester_label: string;
  status_code: string;
  status_label: string;
  current_sequence_order: number;
  current_approver_id: number | null;
  current_approver_label: string | null;
  business_reason: string | null;
  submitted_at: string | null;
  finalized_at: string | null;
}

interface RoleLine {
  /** apr_request_line.line_internal_code — the role code, and the decision key. */
  line_internal_code: string;
  role_label: string;
  status_internal_code: RoleStatus;
  /**
   * Null until the approver chooses. NOT defaulted to APPROVE: the engine
   * refuses a decision leaving any open line unnamed (APR.LINES_INCOMPLETE),
   * because granting a role nobody looked at is the one failure an approval
   * screen must not have. The legacy path defaulted to APPROVE and did exactly that.
   */
  current_decision: Decision | null;
}

interface SignoffLine {
  signoff_id: number;
  sequence_order: number;
  signed_by_label: string;
  decision_comment: string | null;
  signed_at: string;
  per_role: { role_label: string; decision: Decision }[];
}

/** Status → status-pill tone (ok / no / wait / info). */
function statusPillClass(code: string): MobilePillTone {
  switch (code) {
    case 'APPROVED': return 'ok';
    case 'REJECTED': return 'no';
    case 'PENDING': return 'wait';
    default: return 'info'; // CANCELLED / unknown
  }
}

/** Status → lead-accent tone for the list row icon. */
function statusLeadClass(code: string): MobileLeadTone {
  switch (code) {
    case 'APPROVED': return 'green';
    case 'REJECTED': return 'red';
    case 'PENDING': return 'amber';
    default: return 'slate';
  }
}

// ---------------------------------------------------------------------------
// Inbox list
// ---------------------------------------------------------------------------

export function MobileUserProvisionPage({ session }: MobilePageComponentProps) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const meId = session.effective_user.user_id;

  const [rows, setRows] = useState<RequestRow[]>([]);
  const [tab, setTab] = useState<Tab>('pending');
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<RequestRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const locale = activeLocale;
      // Provisioning requests ARE apr_requests now. Two reads rather than one
      // embedded query: the waiting-approver label needs apr_step, and naming FK
      // constraints for an embed would couple this screen to names the engine
      // may change.
      const { data: reqData, error: e } = await apiDb
        .from('apr_request')
        .select(`
          approval_request_id, state, current_level, active_levels,
          staged_payload, requested_by_user_id, requested_at, decided_at,
          config:apr_config!approval_config_id ( internal_code ),
          requester:csm_user_master!requested_by_user_id ( display_name, email )
        `)
        .eq('entity_name', 'csm_user_master')
        .order('approval_request_id', { ascending: false });
      if (e) throw e;

      type AprReqRow = {
        approval_request_id: number;
        state: string;
        current_level: number;
        active_levels: number[] | null;
        staged_payload: { row?: Record<string, unknown> } | null;
        requested_by_user_id: number;
        requested_at: string | null;
        decided_at: string | null;
        config?: { internal_code: string } | null;
        requester?: { display_name: string; email: string } | null;
      };
      const provisionReqs = ((reqData ?? []) as AprReqRow[])
        .filter((r) => r.config?.internal_code === 'CSM.USER_PROVISIONING');
      const reqIds = provisionReqs.map((r) => r.approval_request_id);

      const approverByRequest = new Map<number, { id: number; label: string }>();
      if (reqIds.length > 0) {
        const { data: stepData } = await apiDb
          .from('apr_step')
          .select(`approval_request_id, level, approver_user_id, decision,
                   approver:csm_user_master!approver_user_id ( display_name, email )`)
          .in('approval_request_id', reqIds)
          .is('decision', null);
        type StepRow = {
          approval_request_id: number; level: number; approver_user_id: number;
          approver?: { display_name: string; email: string } | null;
        };
        for (const st of ((stepData ?? []) as StepRow[])) {
          const req = provisionReqs.find((r) => r.approval_request_id === st.approval_request_id);
          const active = req?.active_levels ?? (req ? [req.current_level] : []);
          if (!active.includes(st.level)) continue;
          if (!approverByRequest.has(st.approval_request_id)) {
            approverByRequest.set(st.approval_request_id, {
              id: st.approver_user_id,
              label: st.approver?.display_name ?? st.approver?.email ?? '—',
            });
          }
        }
      }

      // §17.B4: the sheet shows the resolved job name, never the id in the payload.
      const { data: jobData } = await apiDb
        .from('csm_job_description')
        .select('job_description_id, internal_code, csm_job_description_tl ( locale_code, display_text )')
        .eq('is_active', 'Y');
      type JobRow = { job_description_id: number; internal_code: string; csm_job_description_tl?: TlRow[] };
      const pick = (tls: TlRow[] | undefined, fallback: string): string => {
        const list = tls ?? [];
        const exact = list.find((tl) => tl.locale_code === locale);
        const fb = list.find((tl) => tl.locale_code === 'EN_US') ?? list[0];
        return exact?.display_text ?? fb?.display_text ?? fallback;
      };
      const jobLabelById = new Map<number, string>(
        ((jobData ?? []) as JobRow[]).map((j) => [j.job_description_id, pick(j.csm_job_description_tl, j.internal_code)]),
      );

      const list: RequestRow[] = provisionReqs.map((r) => {
        const row = (r.staged_payload?.row ?? {}) as Record<string, unknown>;
        const waiting = approverByRequest.get(r.approval_request_id) ?? null;
        return {
          approval_request_id: r.approval_request_id,
          request_internal_code: `PRV-${String(r.approval_request_id).padStart(5, '0')}`,
          target_email: String(row.email ?? ''),
          target_display_name: String(row.display_name ?? ''),
          target_job_label: jobLabelById.get(Number(row.job_description_id ?? 0)) ?? '',
          requested_by: r.requested_by_user_id,
          requester_label: r.requester?.display_name ?? r.requester?.email ?? '—',
          status_code: r.state,
          status_label: t(`user_provision.state.${r.state}`, r.state),
          current_sequence_order: r.current_level,
          current_approver_id: waiting?.id ?? null,
          current_approver_label: waiting?.label ?? null,
          business_reason: null,
          submitted_at: r.requested_at,
          finalized_at: r.decided_at,
        };
      });
      setRows(list);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (status/job labels
  // follow it — §8).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  const scopedRows = useMemo(() => {
    if (tab === 'pending') {
      return rows.filter((r) => r.current_approver_id === meId && r.status_code === 'PENDING');
    }
    return rows.filter((r) => r.requested_by === meId);
  }, [rows, tab, meId]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return scopedRows;
    return scopedRows.filter((r) =>
      r.request_internal_code.toLowerCase().includes(q) ||
      r.target_email.toLowerCase().includes(q) ||
      r.target_display_name.toLowerCase().includes(q) ||
      r.target_job_label.toLowerCase().includes(q),
    );
  }, [scopedRows, filter]);

  const tabs: { key: Tab; label: string }[] = [
    { key: 'submit', label: t('mobile.provision.tab_submit', 'Submit new user') },
    { key: 'pending', label: t('mobile.provision.scope_pending', 'Pending my approval') },
    { key: 'mine', label: t('mobile.provision.scope_mine', 'My submissions') },
  ];

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.provision.title', 'User Provisioning')}
        description={t('mobile.provision.description', 'Request a new user and approve provisioning requests.')}
      />

      <div className="m-chips">
        {tabs.map((c) => (
          <button
            key={c.key}
            type="button"
            className={'m-chip' + (tab === c.key ? ' m-active' : '')}
            onClick={() => setTab(c.key)}
          >
            {c.label}
          </button>
        ))}
      </div>

      {tab === 'submit' ? (
        <MobileSubmitNewUserForm
          session={session}
          onSubmitted={() => { setTab('mine'); void reload(); }}
        />
      ) : (
        <>
          <div className="m-searchbox">
            <Search />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={t('mobile.provision.search', 'Search requests')}
              aria-label={t('mobile.provision.search', 'Search requests')}
            />
          </div>

          {error && (
            <MobileContentCard>
              <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
            </MobileContentCard>
          )}

          {filtered.length === 0 && !busy ? (
            <MobileContentCard>
              <div className="m-empty">
                <Inbox />{' '}
                {tab === 'pending'
                  ? t('mobile.provision.none_pending', 'Nothing awaiting your approval')
                  : t('mobile.provision.none_mine', 'You have no provisioning requests')}
              </div>
            </MobileContentCard>
          ) : (
            <MobileContentCard list>
              {filtered.map((r) => (
                <MobileCardRow
                  key={r.approval_request_id}
                  lead={<UserPlus />}
                  leadTone={statusLeadClass(r.status_code)}
                  title={r.target_display_name || r.target_email}
                  subtitle={`${r.target_job_label || r.target_email} · ${r.requester_label}`}
                  trailing={<MobileStatusPill tone={statusPillClass(r.status_code)}>{r.status_label}</MobileStatusPill>}
                  chevron
                  onClick={() => setSelected(r)}
                />
              ))}
            </MobileContentCard>
          )}
        </>
      )}

      <MobileProvisionDetailSheet
        session={session}
        request={selected}
        onClose={() => setSelected(null)}
        onChanged={() => { setSelected(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Detail bottom-sheet — request summary + requested roles (per-role
// approve/reject when it's my turn) + approval history. Decisions submit through
// apr_decide_lines (§2 sanctioned path).
// ---------------------------------------------------------------------------

interface MobileProvisionDetailSheetProps {
  session: AuthSession;
  request: RequestRow | null;
  onClose: () => void;
  onChanged: () => void;
}

function MobileProvisionDetailSheet({ session, request, onClose, onChanged }: MobileProvisionDetailSheetProps) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const formatDateTime = useFormatDateTime();
  const meId = getForensicActorId(session);
  const isMyTurn = request != null && request.current_approver_id === meId && request.status_code === 'PENDING';

  const [roles, setRoles] = useState<RoleLine[]>([]);
  const [signoffs, setSignoffs] = useState<SignoffLine[]>([]);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load roles + sign-off history whenever a different request is opened. Depends
  // only on the request id + active locale — never on `t` (a fresh function each
  // render would loop the effect in tests).
  const requestId = request?.approval_request_id ?? null;
  useEffect(() => {
    if (requestId == null) { setRoles([]); setSignoffs([]); setComment(''); setError(null); return; }
    let cancelled = false;
    setComment('');
    setError(null);

    void (async () => {
      // No _tl resolution needed here any more: apr_request_line stores the label
      // already resolved at submit time.
      const { data: rData, error: rErr } = await apiDb
        .from('apr_request_line')
        .select('line_internal_code, line_label, state, sort_order')
        .eq('approval_request_id', requestId)
        .order('sort_order');
      if (cancelled) return;
      if (rErr) { setError(rErr.message); return; }

      type LineDataRow = { line_internal_code: string; line_label: string; state: string };
      // line_label was resolved through _tl at submit time, so no join here and
      // no chance of a bare code reaching the approver (§17.B4).
      const rs: RoleLine[] = ((rData ?? []) as LineDataRow[]).map((r) => ({
        line_internal_code: r.line_internal_code,
        role_label: r.line_label,
        status_internal_code: r.state as RoleStatus,
        current_decision: null,
      }));
      setRoles(rs);

      const { data: sData, error: sErr } = await apiDb
        .from('apr_step')
        .select(`
          approval_step_id, level, decided_at, decision, decision_comment,
          approver:csm_user_master!approver_user_id ( display_name, email ),
          lines:apr_step_line ( decision,
            line:apr_request_line!approval_request_line_id ( line_label ) )
        `)
        .eq('approval_request_id', requestId)
        .order('level');
      if (cancelled) return;
      if (sErr) { setError(sErr.message); return; }

      type StepLineDataRow = { decision: string; line?: { line_label: string } | null };
      type StepDataRow = {
        approval_step_id: number;
        level: number;
        decided_at: string | null;
        decision_comment: string | null;
        approver?: { display_name: string; email: string } | null;
        lines?: StepLineDataRow[];
      };
      // Only steps someone actually ruled on are history; the apiDb builder has
      // no `.not()`, so undecided ones are dropped here.
      const so: SignoffLine[] = ((sData ?? []) as StepDataRow[])
        .filter((st) => st.decided_at != null)
        .map((st) => ({
          signoff_id: st.approval_step_id,
          sequence_order: st.level,
          signed_by_label: st.approver?.display_name ?? st.approver?.email ?? '—',
          decision_comment: st.decision_comment,
          signed_at: st.decided_at as string,
          per_role: (st.lines ?? []).map((l) => ({
            role_label: l.line?.line_label ?? '',
            decision: l.decision as Decision,
          })),
        }));
      setSignoffs(so);
    })();

    return () => { cancelled = true; };
  }, [requestId, activeLocale]);

  const setRoleDecision = (code: string, dec: Decision) => {
    setRoles((prev) => prev.map((r) =>
      r.line_internal_code === code ? { ...r, current_decision: dec } : r,
    ));
  };

  const roleStatusLabel = (code: RoleStatus): string =>
    code === 'APPROVED' ? t('approval.role_status.approved', 'Approved')
      : code === 'REJECTED' ? t('approval.role_status.rejected', 'Rejected')
        : t('approval.role_status.pending', 'Pending');

  const submit = async () => {
    if (!request) return;
    setBusy(true);
    setError(null);
    try {
      const open = roles.filter((r) => r.status_internal_code === 'PENDING');
      if (open.length === 0) {
        throw new Error(t('mobile.provision.no_pending_roles', 'No pending roles to act on.'));
      }
      // Every open role needs an explicit verdict. The server enforces it too
      // (APR.LINES_INCOMPLETE); naming the stragglers here beats an error code.
      const undecided = open.filter((r) => r.current_decision == null);
      if (undecided.length > 0) {
        throw new Error(t('user_provision.detail.err_undecided',
          'Decide every role before submitting. Still undecided: {{roles}}',
          { roles: undecided.map((r) => r.role_label).join(', ') }));
      }
      // §2 sanctioned path: apr_decide_lines records the per-role verdicts, then
      // the engine advances/finalizes and commits through the Common Routine.
      const { data: res, error: e } = await apiDb.rpc<{ ok: boolean; error_code?: string }>(
        'apr_decide_lines', {
          p_request_id: request.approval_request_id,
          p_lines: open.map((r) => ({
            line_internal_code: r.line_internal_code,
            decision: r.current_decision,
          })),
          p_comment: comment.trim() || null,
        });
      if (e) throw e;
      if (!res?.ok) {
        throw new Error(t(`user_provision.error.${(res?.error_code ?? '').replace(/\./g, '_')}`,
                          t('mobile.provision.decide_failed', 'Decision failed.')));
      }
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const open = request !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.provision.detail', 'Provisioning request')}>
        <div className="m-grabber" />
        {request && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><UserPlus size={20} /></span>
              <span>
                <div className="m-nm">{request.target_display_name || request.target_email}</div>
                <div className="m-em">{request.request_internal_code}</div>
              </span>
            </div>

            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.provision.status', 'Status')}
                value={<MobileStatusPill tone={statusPillClass(request.status_code)}>{request.status_label}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.provision.email', 'Email')} value={request.target_email} />
              <MobileDetailRow label={t('mobile.provision.job', 'Job')} value={request.target_job_label || t('common.none', 'None')} />
              <MobileDetailRow label={t('mobile.provision.requested_by', 'Requested by')} value={request.requester_label} />
              <MobileDetailRow label={t('mobile.provision.awaiting', 'Awaiting')} value={request.current_approver_label ?? t('mobile.provision.finalized', 'Finalized')} />
              {request.submitted_at && (
                <MobileDetailRow label={t('mobile.provision.submitted_at', 'Submitted')} value={formatDateTime(request.submitted_at)} />
              )}
            </MobileContentCard>
            {/* §17.B13 — the row's related records, stacked one group per block. */}
            <MobileContentCard>
              <MobileRelatedChildren
                session={session}
                groups={[
                  { key: 'job', table: 'csm_job_description', pkColumn: 'job_description_id',
                    title: t('mobile.user_provision.related.job', 'Job'),
                    items: request.target_job_label ? [{ id: 'job', label: request.target_job_label }] : [] },
                  { key: 'awaiting',
                    title: t('mobile.user_provision.related.awaiting', 'Awaiting'),
                    items: request.current_approver_label ? [{ id: 'apr', label: request.current_approver_label }] : [] },
                  { key: 'requester', table: 'csm_user_master', pkColumn: 'user_id',
                    title: t('mobile.user_provision.related.requester', 'Requested by'),
                    items: request.requester_label && request.requester_label !== '—'
                      ? [{ id: request.requested_by, label: request.requester_label }] : [] },
                ]}
              />
            </MobileContentCard>

            {request.business_reason && (
              <div className="m-fieldcard">
                <div className="m-formrow">
                  <label>{t('mobile.provision.business_reason', 'Business reason')}</label>
                  <div className="m-formrow-ro">{request.business_reason}</div>
                </div>
              </div>
            )}

            {/* Requested roles — per-role Approve/Reject when it's my turn. */}
            <div className="m-fieldcard">
              <div className="m-formrow">
                <label>{t('mobile.provision.requested_roles', 'Requested roles')}</label>
              </div>
              {roles.length === 0 ? (
                <div className="m-field"><span className="m-v" style={{ opacity: 0.6 }}>{t('common.none', 'None')}</span></div>
              ) : roles.map((r) => (
                <div key={r.line_internal_code} className="m-field">
                  <span className="m-k">{r.role_label}</span>
                  <span className="m-v">
                    {isMyTurn && r.status_internal_code === 'PENDING' ? (
                      <span className="m-chips" style={{ margin: 0, justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          className={'m-chip' + (r.current_decision === 'APPROVE' ? ' m-active' : '')}
                          onClick={() => setRoleDecision(r.line_internal_code, 'APPROVE')}
                        >
                          {t('mobile.provision.approve', 'Approve')}
                        </button>
                        <button
                          type="button"
                          className={'m-chip' + (r.current_decision === 'REJECT' ? ' m-active' : '')}
                          onClick={() => setRoleDecision(r.line_internal_code, 'REJECT')}
                        >
                          {t('mobile.provision.reject', 'Reject')}
                        </button>
                      </span>
                    ) : (
                      <MobileStatusPill tone={statusPillClass(r.status_internal_code)}>
                        {roleStatusLabel(r.status_internal_code)}
                      </MobileStatusPill>
                    )}
                  </span>
                </div>
              ))}
            </div>

            {/* Approval history */}
            {signoffs.length > 0 && (
              <div className="m-fieldcard">
                <div className="m-formrow">
                  <label>{t('mobile.provision.history', 'Approval history')}</label>
                </div>
                {signoffs.map((s) => (
                  <div key={s.signoff_id} className="m-field">
                    <span className="m-k">
                      {s.sequence_order === 0
                        ? t('mobile.provision.manager_step', 'Manager')
                        : t('mobile.provision.step_n', 'Step {{n}}', { n: s.sequence_order })}
                    </span>
                    <span className="m-v" style={{ textAlign: 'right' }}>
                      <div>{s.signed_by_label}</div>
                      <div className="m-em">{formatDateTime(s.signed_at)}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end', marginTop: 4 }}>
                        {s.per_role.map((p, i) => (
                          <span key={i} className={'m-pill ' + (p.decision === 'APPROVE' ? 'm-p-ok' : 'm-p-no')}>
                            {(p.decision === 'APPROVE'
                              ? t('mobile.provision.approve', 'Approve')
                              : t('mobile.provision.reject', 'Reject'))} · {p.role_label}
                          </span>
                        ))}
                      </div>
                      {s.decision_comment && <div className="m-em" style={{ marginTop: 4 }}>“{s.decision_comment}”</div>}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {isMyTurn && (
              <div className="m-fieldcard">
                <div className="m-formrow">
                  <label htmlFor="m-provision-comment">{t('mobile.provision.comment', 'Comment (optional)')}</label>
                  <textarea
                    id="m-provision-comment"
                    className="m-inp"
                    rows={2}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={t('mobile.provision.comment_placeholder', 'Add a note for the requester…')}
                  />
                </div>
              </div>
            )}

            {error && <MobileContentCard><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></MobileContentCard>}

            <div className="m-actionbar">
              {isMyTurn && (
                <>
                  <button
                    type="button"
                    className="m-btn m-btn-primary"
                    disabled={busy}
                    onClick={() => void submit()}
                  >
                    <Check size={16} />
                    {busy ? t('common.saving', 'Saving…') : t('mobile.provision.submit_decisions', 'Submit decisions')}
                  </button>
                  <button
                    type="button"
                    className="m-btn m-btn-danger"
                    disabled={busy}
                    onClick={() => { setRoles((prev) => prev.map((r) => r.status_internal_code === 'PENDING' ? { ...r, current_decision: 'REJECT' } : r)); }}
                    aria-label={t('mobile.provision.reject_all', 'Reject all')}
                  >
                    <Ban size={16} />
                    {t('mobile.provision.reject_all', 'Reject all')}
                  </button>
                </>
              )}
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
